import * as Yup from "yup";
import { QrReader } from "react-qr-reader";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  CircularProgress,
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  MenuItem,
} from "@mui/material";
import { OuterBox } from "../../components";
import UserDetails from "./UserDetails";
import { useAxios } from "../../hooks";
import { setDisplayName } from "../../redux/slices/memberSlice";
import ImgCapture from "./ImgCapture";
import { useLocation } from "react-router-dom";
import LoadingButton from "../../components/LoadingButton";

const BookingStats = () => {
  const axios = useAxios({ baseURL: "mox_api_2" });
  const dispatch = useDispatch();
  const { authToken } = useSelector((state) => state.memberDetails);
  const videoRef = useRef(null);
  const location = useLocation();

  // ------------------ States ------------------
  const [mode, setMode] = useState("qr"); // "qr" | "face"
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [cameraScreen, setCameraScreen] = useState("environment");
  const [userStep, setUserStep] = useState(""); // "" | "QRcode"

  // Entity/Event selection
  const [entityType, setEntityType] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [formValues, setFormValues] = useState({
    entityType: "",
    eventID: "",
    name: "",
  });

  const [gates, setGates] = useState([]);
  const [selectedGate, setSelectedGate] = useState(
    Number(sessionStorage.getItem("gateMate")) || 0
  );

  // ------------------ Fetch initial data ------------------
  // useEffect(() => {
  //   axios({
  //     url: "/visitor/dome-list",
  //     disableRedirect: true,
  //     disableError: true,
  //   }).then((response) => {
  //     if (response.status) setGates(response.data);
  //   });
  // }, []);

  useEffect(() => {
    axios({
      url: "entry/entry/entity-type",
      disableRedirect: true,
      disableError: true,
    }).then((response) => {
      if (response.status) setEntityType(response.data);
    });
  }, []);

  // ------------------ Handlers ------------------
  const handleEntityType = (e) => {
    const value = e.target.value;
    setFormValues({ ...formValues, entityType: value, eventID: "" });

    axios({
      url: "/entry/entry/entity-list?entity_type=" + value,
      disableRedirect: true,
      disableError: true,
    }).then((response) => {
      if (response.status) {
        setEventList(response.data);
      } else {
        setEventList([]);
      }
    });
  };

  const handleEventId = (e) => {
    axios({
      url: `entry/entry/machine-list?entity_id=${e.target.value}`,
      disableRedirect: true,
      disableError: true,
      customAuthToken: "9beaa95b-c59f-4ec9-bb60-5f4ee1986311",
    }).then((response) => {
      if (response.status) {
        setGates(response.data);
      }
    });
    setFormValues({ ...formValues, eventID: e.target.value });
  };

  // 📌 Fetch user details (QR or Face API)
  async function fetchUserDetails({ bookingId, faceImage }) {
    setLoading(true);
    try {
      const payload = {
        user_id: bookingId,
        face_data: faceImage,
        machine_id: formValues.selectedGate,
        gate_type: "entry",
      };
      const response = await axios({
        url: "entry/entry",
        method: "POST",
        data: payload,
        customAuthToken: authToken,
        disableRedirect: true,
      });

      if (response.status) {
        setUserDetails(response.data);
      } else {
        setUserDetails(null);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResendNotification() {
    setLoading(true);
    const res = await axios({
      url: "/entry/entry/resend-notification",
      method: "POST",
      data: { visitor_id: userDetails.visitor_id },
      customAuthToken: authToken,
    });
    setLoading(false);
    if (res.status) resetProcess();
  }

  async function handleIssueBadge() {
    setLoading(true);
    const res = await axios({
      url: "/entry/entry/band-issue",
      method: "POST",
      data: { booking_id: userDetails.booking_id },
      customAuthToken: authToken,
    });
    setLoading(false);
    if (res.status) resetProcess();
  }

  function resetProcess() {
    setUserDetails(null); // clear user details
    setMode("qr"); // reset to qr scanner
  }

  useEffect(() => {
    return () => {
      if (videoRef.current && videoRef.current?.srcObject) {
        const tracks = videoRef.current.srcObject?.getTracks();
        tracks.forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, [location]);

  // ------------------ JSX ------------------
  return (
    <OuterBox>
      {/* Step 1: Entity & Event Selection */}
      {userStep === "" && (
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12}>
            <TextField
              select
              size="small"
              fullWidth
              name="entityType"
              label="Select Entity Type"
              value={formValues.entityType}
              onChange={handleEntityType}
            >
              {entityType.map((item, index) => (
                <MenuItem key={index} value={item.entity_id}>
                  {item.name?.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              size="small"
              fullWidth
              name="eventID"
              label="Select Event Name"
              value={formValues.eventID}
              onChange={handleEventId}
              disabled={!formValues.entityType}
            >
              {eventList?.map((item, index) => (
                <MenuItem key={index} value={item?.entity_id}>
                  {item?.name?.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              name="gateNo"
              size="small"
              onChange={(e) => {
                setFormValues({ ...formValues, selectedGate: e.target.value });
              }}
              value={formValues?.selectedGate}
            >
              <MenuItem value={0}>Select Your Current Gate</MenuItem>
              {gates.map((item) => {
                return (
                  <MenuItem value={item.machine_id}>{item.zone_name}</MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <LoadingButton
              variant="contained"
              fullWidth
              disabled={!formValues.selectedGate}
              onClick={() => {
                dispatch(setDisplayName(formValues.name || "Guest"));
                setUserStep("QRcode");
              }}
            >
              Submit
            </LoadingButton>
          </Grid>
        </Grid>
      )}

      {/* Step 2: QR/Face Mode */}
      {userStep === "QRcode" && (
        <>
          {/* Show switch ONLY if userDetails is not available */}
          {!userDetails && (
            <Grid container justifyContent="center" mt={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={mode === "face"}
                    onChange={(e) => setMode(e.target.checked ? "face" : "qr")}
                  />
                }
                label={mode === "qr" ? "QR Code Mode" : "Face Photo Mode"}
              />
            </Grid>
          )}

          {/* QR Mode */}
          {mode === "qr" && !userDetails && !loading && (
            <Grid container justifyContent="center" mt={5}>
              <Grid item sx={{ width: "60vh" }}>
                <QrReader
                  style={{ width: "100%" }}
                  onResult={(result, error) => {
                    if (!!result) {
                      fetchUserDetails({ bookingId: result?.text });
                    }
                    if (!!error) console.info(error);
                  }}
                  constraints={{ facingMode: "environment" }}
                  scanDelay={300}
                />
              </Grid>
            </Grid>
          )}

          {/* Face Mode */}
          {mode === "face" && !userDetails && !loading && (
            <Grid container justifyContent="center" mt={5}>
              <ImgCapture
                loading={loading}
                videoRef={videoRef}
                cameraScreen={cameraScreen}
                onSubmit={fetchUserDetails}
              />
            </Grid>
          )}

          {/* Loader */}
          {loading && (
            <Grid container justifyContent="center" mt={10}>
              <CircularProgress sx={{ color: "green" }} />
              <Typography sx={{ mt: 2 }}>Fetching User Details...</Typography>
            </Grid>
          )}

          {/* User Details + Back Button */}
          {userDetails && (
            <Fragment>
              <UserDetails
                loading={loading}
                userDetails={userDetails}
                handleIssueBadge={handleIssueBadge}
                handleResendNotification={handleResendNotification}
              />

              <Grid container justifyContent="center" mt={2}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={resetProcess}
                  sx={{ borderRadius: 3, textTransform: "none", px: 4 }}
                >
                  Back
                </Button>
              </Grid>
            </Fragment>
          )}
        </>
      )}
    </OuterBox>
  );
};

export default BookingStats;
